import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  User,
  Workspace,
  SentryIssue,
  ProcessedIssue,
  AuthTokens,
  LoginRequest,
  RegisterRequest,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  ProcessIssueRequest,
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private baseURL = process.env.REACT_APP_API_URL || '/api/v1';

  constructor() {
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async login(credentials: LoginRequest): Promise<AuthTokens> {
    const response: AxiosResponse<AuthTokens> = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async register(userData: RegisterRequest): Promise<AuthTokens> {
    const response: AxiosResponse<AuthTokens> = await this.api.post('/auth/register', userData);
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get('/auth/me');
    return response.data;
  }

  async createWorkspace(workspaceData: CreateWorkspaceRequest): Promise<Workspace> {
    const response: AxiosResponse<{ workspace: Workspace }> = await this.api.post('/workspaces/', workspaceData);
    return response.data.workspace;
  }

  async getCurrentWorkspace(): Promise<Workspace> {
    const response: AxiosResponse<Workspace> = await this.api.get('/workspaces/current');
    return response.data;
  }

  async updateWorkspace(workspaceData: UpdateWorkspaceRequest): Promise<Workspace> {
    const response: AxiosResponse<Workspace> = await this.api.put('/workspaces/current', workspaceData);
    return response.data;
  }

  async testSentryConnection(testData: { sentry_api_token: string; sentry_organization: string }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/workspaces/test-sentry', testData);
    return response.data;
  }

  async testOpenAIConnection(testData: { openai_api_key: string }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/workspaces/test-openai', testData);
    return response.data;
  }

  async getSentryProjects(): Promise<any[]> {
    const response: AxiosResponse<any[]> = await this.api.get('/issues/projects');
    return response.data;
  }

  async getSentryIssues(projectId?: string, query?: string): Promise<SentryIssue[]> {
    const params = new URLSearchParams();
    if (projectId) params.append('project_id', projectId);
    if (query) params.append('query', query);
    params.append('limit', '100');
    
    const response: AxiosResponse<any> = await this.api.get(`/issues/?${params}`);
    return response.data.issues || [];
  }

  async getSentryIssue(issueId: string): Promise<SentryIssue> {
    const response: AxiosResponse<SentryIssue> = await this.api.get(`/issues/${issueId}`);
    return response.data;
  }

  async analyzeIssue(issueId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/issues/${issueId}/analyze`);
    return response.data;
  }

  async processIssue(issueData: ProcessIssueRequest): Promise<ProcessedIssue> {
    const response: AxiosResponse<ProcessedIssue> = await this.api.post(`/issues/${issueData.sentry_issue_id}/analyze`, {
      generate_specification: issueData.generate_specification
    });
    return response.data;
  }

  async getProcessedIssues(): Promise<ProcessedIssue[]> {
    const response: AxiosResponse<ProcessedIssue[]> = await this.api.get('/issues/processed/');
    return response.data;
  }

  async getProcessedIssue(issueId: string): Promise<ProcessedIssue> {
    const response: AxiosResponse<ProcessedIssue> = await this.api.get(`/issues/processed/${issueId}`);
    return response.data;
  }

  async deleteProcessedIssue(issueId: string): Promise<void> {
    await this.api.delete(`/issues/processed/${issueId}`);
  }

  async getSettings(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/settings/');
    return response.data;
  }

  async updateSettings(settings: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put('/settings/', settings);
    return response.data;
  }

  async getDebugInfo(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/debug/info');
    return response.data;
  }

  async healthCheck(): Promise<any> {
    const response: AxiosResponse<any> = await axios.get('/health');
    return response.data;
  }

  async generateRandomSentryEvent(eventType?: string, count?: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/sentry/generate-random-event', {
      event_type: eventType,
      count: count
    });
    return response.data;
  }

  async getSentryStatus(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/sentry/sentry-status');
    return response.data;
  }

  async getSentryStatusPublic(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/sentry/sentry-status-public');
    return response.data;
  }

  async getEventTemplates(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/sentry/event-templates');
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
